import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

import { RouteManager } from '../../RouteManager';
import { HomeyAppVersionError, HomeySystemVersionError } from '../../lib/InternalError';
import { ToastManager } from '../../ToastManager';
import { scriptsStore } from './ScriptsStore';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useScriptRouteId } from './useScriptRouteId';
import { useApi } from '../../store/SelectedHomeyStore';
import { useScripts } from './useScripts';

import { theme } from '../../theme/theme';

import { Layout } from '../../Layout';
import { GradientButton } from '../../components/buttons/GradientButton';
import { SpinnerFade } from '../../components/common/SpinnerFade';
import { CenteredMessage } from '../../components/common/CenteredMessage';
import { SpinnerLoading } from '../../components/common/SpinnerLoading';

import { ScriptNavigation } from './ScriptNavigation';
import { ScriptEditor } from './ScriptEditor';
import { ScriptActions } from './ScriptActions';
import { ScriptDialog } from './ScriptDialog';

import { iconHomeyScript } from '../../theme/icons/system/homeyscript';
import { iconDownload } from '../../theme/icons/interface/download';

export function ScriptPage() {
  const editorInstanceRef = useRef();
  const installIntervalRef = useRef(null);
  const location = useLocation();

  const { i18n } = useI18n();
  const { api } = useApi();
  const [isInstalling, setIsInstalling] = useState(false);

  const { app, isLoading, error } = useScripts();

  const { scriptRouteId } = useScriptRouteId();

  useEffect(() => {
    if (isLoading || isInstalling || error || scriptRouteId == null) {
      return;
    }

    scriptsStore.getScript({ scriptId: scriptRouteId }).catch(ToastManager.handleError);
  }, [isLoading, isInstalling, error, scriptRouteId]);

  function handleInstallPress() {
    if (installIntervalRef.current != null) {
      return;
    }

    setIsInstalling(true);

    let count = 0;
    installIntervalRef.current = setInterval(() => {
      count += 1;
      const state = scriptsStore.get();

      if (state.appInstance != null && state.appInstance.ready === true) {
        setIsInstalling(false);
        clearInterval(installIntervalRef.current);
        installIntervalRef.current = null;
        scriptsStore.refresh().catch(ToastManager.handleError);
        return;
      }

      if (count >= 30) {
        ToastManager.handleError(new Error('Installation timed out'));
        clearInterval(installIntervalRef.current);
        installIntervalRef.current = null;
        setIsInstalling(false);
      }
    }, 1000);

    api.apps
      .installFromAppStore({
        id: 'com.athom.homeyscript',
      })
      .then((result) => {})
      .catch((error) => {
        ToastManager.handleError(error);
        setIsInstalling(false);
        clearInterval(installIntervalRef.current);
        installIntervalRef.current = null;
      })
      .finally(() => {});
  }

  let content = null;

  switch (true) {
    case error?.message === 'app_not_found':
    case error?.message === 'Not Found: App with ID com.athom.homeyscript':
    case error == null && app == null && isLoading === false:
      content = (
        <CenteredMessage
          maskColor={theme.color.text_light}
          mask={iconHomeyScript}
          titleColor={theme.color.text_light}
          title={i18n.messageFormatter('script.installTitle')}
          subtitleColor={theme.color.text_light}
          subtitle={i18n.messageFormatter('script.installSubtitle')}
          action={
            <GradientButton onPress={handleInstallPress}>
              <SpinnerFade
                isActive={isInstalling === true}
                size={theme.icon.size_small}
                color={theme.color.white}
              >
                <GradientButton.Icon
                  url={iconDownload}
                  size={theme.icon.size_small}
                  color={theme.color.white}
                />
              </SpinnerFade>
              <GradientButton.Text>
                {isInstalling === true
                  ? i18n.messageFormatter('script.installing')
                  : i18n.messageFormatter('script.install')}
              </GradientButton.Text>
            </GradientButton>
          }
        />
      );
      break;

    case error instanceof HomeySystemVersionError:
      content = (
        <CenteredMessage
          titleColor={theme.color.text_light}
          title={i18n.messageFormatter('script.invalidHomeyVersionTitle')}
          subtitleColor={theme.color.text_light}
          subtitle={i18n.messageFormatter('script.invalidHomeyVersionSubtitle')}
        />
      );
      break;

    case error instanceof HomeyAppVersionError:
      content = (
        <CenteredMessage
          titleColor={theme.color.text_light}
          title={i18n.messageFormatter('script.invalidScriptVersionTitle')}
          subtitleColor={theme.color.text_light}
          subtitle={i18n.messageFormatter('script.invalidScriptVersionSubtitle')}
          action={
            <GradientButton onPress={handleInstallPress}>
              <SpinnerFade
                isActive={isInstalling === true}
                size={theme.icon.size_small}
                color={theme.color.white}
              >
                <GradientButton.Icon
                  url={iconDownload}
                  size={theme.icon.size_small}
                  color={theme.color.white}
                />
              </SpinnerFade>
              <GradientButton.Text>
                {isInstalling === true
                  ? i18n.messageFormatter('script.installing')
                  : i18n.messageFormatter('script.install')}
              </GradientButton.Text>
            </GradientButton>
          }
        />
      );
      break;

    case isLoading === true:
      content = (
        <CenteredMessage
          icon={<SpinnerLoading color={theme.color.text_light} size={40} />}
          subtitleColor={theme.color.text_light}
          subtitle={i18n.messageFormatter('status.loading')}
        />
      );
      break;

    case scriptRouteId == null:
      content = (
        <CenteredMessage
          maskColor={theme.color.text_light}
          mask={iconHomeyScript}
          titleColor={theme.color.text_light}
          title={i18n.messageFormatter('script.selectTitle')}
          subtitleColor={theme.color.text_light}
          subtitle={i18n.messageFormatter('script.selectSubtitle')}
        />
      );
      break;

    default:
      content = <ScriptEditor editorInstanceRef={editorInstanceRef} />;
      break;
  }

  const isReady = isLoading === false && isInstalling === false && error == null;
  const showActions = isReady && scriptRouteId != null;
  const subNavigation = isReady
    ? {
        settings: {
          key: 'scripts',
          width: 280,
          title: 'HomeyScript',
        },
        component: <ScriptNavigation />,
      }
    : null;

  return (
    <Layout
      subNavigationProps={subNavigation?.settings}
      subNavigationChildren={subNavigation?.component}
      actions={showActions && <ScriptActions editorInstanceRef={editorInstanceRef} />}
      content={content}
    >
      <AnimatePresence>
        {isReady && location.state?.[RouteManager.dialogState.newScriptDialog] === true && (
          <ScriptDialog />
        )}
      </AnimatePresence>
    </Layout>
  );
}

export default ScriptPage;
